import { useInfiniteQuery } from '@tanstack/react-query';

import { getRecommendedTracks } from '@/api/track';
import type { RecommendedTracksResponse } from '@/types';

/**
 * Infinite discover feed for the selected circle.
 *
 * NOTE: the legacy "all my groups" path (`filters[groups][memberships][user]`)
 * depends on the Groups feature, which isn't live on the backend yet (the
 * `/groups/*` routes 404). So the web home defaults to the **public** feed
 * (`is_public=true`) when no real circle is selected, and only uses the group
 * filter when a real group id is provided. We never send the membership path.
 */
export function discoverQueryKey(
  selectedGroupId: number | null,
  includePublic: boolean,
  styles: readonly string[],
) {
  // feed-genre-filter — the selection is part of the cache identity, or toggling
  // a chip would serve the previous page from cache and the filter would look
  // broken. Sorted so the same SET is the same query whatever the tap order.
  return ['discover-track', selectedGroupId, includePublic, [...styles].sort().join(',')] as const;
}

export function useDiscoverFeed(params: {
  selectedGroupId: number | null;
  includePublic: boolean;
  styles?: readonly string[];
}) {
  const { selectedGroupId, includePublic, styles = [] } = params;

  return useInfiniteQuery<RecommendedTracksResponse, Error>({
    queryKey: discoverQueryKey(selectedGroupId, includePublic, styles),
    queryFn: ({ pageParam }) =>
      getRecommendedTracks(pageParam as number, null, selectedGroupId, includePublic, styles),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pageCount } = lastPage.meta.pagination;
      return page < pageCount ? page + 1 : undefined;
    },
    retry: 1,
  });
}
